import React, { useState } from "react";
import { FileQuestion, Loader2, ChevronDown, ChevronUp, Download } from "lucide-react";
import { API_CONFIG } from "../config/api";

const CandidateQuestions = ({ candidate, selectedRole }) => {
  const [questions, setQuestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuestionCount, setCurrentQuestionCount] = useState(0);
  const MAX_QUESTIONS = 30;
  const INITIAL_QUESTIONS = 10;
  const INCREMENT_QUESTIONS = 5;

  // Questions are NOT saved to DynamoDB - removed per user request

  const generateQuestions = async (numQuestions = INITIAL_QUESTIONS) => {
    if (!candidate || !selectedRole) return;

    setIsLoading(true);
    setError(null);

    try {
      // CRITICAL: Use the actual resume file name, not the display name
      // Priority: resumeFileName > fileName > name
      const searchName = candidate.resumeFileName || candidate.fileName || candidate.name;
      console.log("=== QUESTION GENERATION DEBUG ===");
      console.log("Candidate object:", candidate);
      console.log("Search name to use:", searchName);
      console.log("Selected role:", selectedRole);
      console.log("Candidate rank:", candidate.rank);
      console.log("Candidate score:", candidate.score);
      
      // FIXED: Get resumes for the specific role using /get-resumes endpoint
      const folderName = selectedRole.replace(/\/$/, ''); // Remove trailing slash
      console.log("Fetching resumes for role:", folderName);
      console.log("API URL:", API_CONFIG.RESUME_API);
      
      // Load all resumes for this role (paginated)
      let allResumes = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100;
      
      while (hasMore && page <= 20) { // Max 20 pages = 2000 resumes
        const resumesResponse = await fetch(`${API_CONFIG.RESUME_API}/get-resumes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folder_name: folderName,
            page: page,
            page_size: pageSize
          })
        });
        
        if (!resumesResponse.ok) {
          throw new Error(`Failed to fetch resumes: ${resumesResponse.status} ${resumesResponse.statusText}`);
        }
        
        const data = await resumesResponse.json();
        allResumes = [...allResumes, ...(data.resumes || [])];
        hasMore = data.has_more;
        page++;
        
        console.log(`Loaded page ${page-1}: ${data.resumes_in_batch} resumes (Total: ${allResumes.length}/${data.total_resumes})`);
        
        if (!hasMore) break;
      }
      
      const resumes = allResumes;
      console.log("Total resumes loaded:", resumes.length);
      console.log("Looking for candidate with search name:", searchName);
      console.log("Candidate name type:", typeof searchName);
      console.log("Candidate name length:", searchName?.length);
      
      // Find the matching resume with VERY aggressive matching logic
      const resume = resumes.find(r => {
        const fileName = (r.file_name || '').trim();
        const fileId = (r.id || '').trim();
        const candidateName = (searchName || '').trim();
        
        // Helper function to normalize strings for comparison
        const normalize = (str) => {
          return str
            .toLowerCase()
            .replace(/\.pdf$/i, '') // Remove .pdf extension
            .replace(/[\s_-]+/g, '') // Remove spaces, underscores, dashes
            .replace(/[^\w]/g, ''); // Remove all non-alphanumeric characters
        };
        
        const normalizedFileName = normalize(fileName);
        const normalizedCandidateName = normalize(candidateName);
        
        // Log first few comparisons for debugging
        if (resumes.indexOf(r) < 3) {
          console.log(`Comparing: "${fileName}" (normalized: "${normalizedFileName}") vs "${candidateName}" (normalized: "${normalizedCandidateName}")`);
        }
        
        // Direct matches
        if (fileName === candidateName) return true;
        if (fileId === candidateName) return true;
        
        // Case-insensitive matches
        if (fileName.toLowerCase() === candidateName.toLowerCase()) return true;
        if (fileId.toLowerCase() === candidateName.toLowerCase()) return true;
        
        // Remove .pdf extension and compare
        const fileNameWithoutExt = fileName.replace(/\.pdf$/i, '');
        const candidateNameWithoutExt = candidateName.replace(/\.pdf$/i, '');
        if (fileNameWithoutExt.toLowerCase() === candidateNameWithoutExt.toLowerCase()) return true;
        
        // Normalized comparison (most aggressive)
        if (normalizedFileName === normalizedCandidateName) return true;
        
        // Partial matches
        if (fileName && candidateName.toLowerCase().includes(fileName.toLowerCase())) return true;
        if (candidateName && fileName.toLowerCase().includes(candidateName.toLowerCase())) return true;
        
        return false;
      });

      console.log("Resume match found:", resume ? resume.file_name : "NO MATCH");
      
      if (!resume) {
        console.error("❌ Exact match failed. Looking for:", searchName);
        console.error("Candidate object:", candidate);
        console.error("Available resume file names (first 30):");
        resumes.slice(0, 30).forEach((r, idx) => {
          console.error(`  ${idx + 1}. "${r.file_name}"`);
        });
        
        // FALLBACK: Try to find the best match by checking if any resume file name contains the search terms
        console.log("🔍 Attempting fuzzy search...");
        
        // Extract key terms from search name (remove common words, split by spaces/underscores)
        const searchTerms = searchName
          .toLowerCase()
          .replace(/\.pdf$/i, '')
          .replace(/[_-]/g, ' ')
          .split(/\s+/)
          .filter(term => term.length > 2 && !['resume', 'cv', 'devops', 'engineer'].includes(term));
        
        console.log("Search terms:", searchTerms);
        
        // Find resumes that contain ALL search terms
        const matches = resumes.filter(r => {
          const fileName = r.file_name.toLowerCase();
          return searchTerms.every(term => fileName.includes(term));
        });
        
        console.log(`Found ${matches.length} potential matches:`, matches.map(m => m.file_name));
        
        // If no matches, show files that contain ANY of the search terms for debugging
        if (matches.length === 0) {
          console.log("🔍 Files containing ANY search term:");
          searchTerms.forEach(term => {
            const partialMatches = resumes.filter(r => r.file_name.toLowerCase().includes(term));
            console.log(`  "${term}": ${partialMatches.length} files`, partialMatches.slice(0, 5).map(m => m.file_name));
          });
        }
        
        if (matches.length === 1) {
          console.log("✅ Found single match:", matches[0].file_name);
          // Use the single match
          const matchedResume = matches[0];
          
          if (!matchedResume.resume_text || matchedResume.resume_text.trim().length === 0) {
            throw new Error("Resume text is empty. Please reload resumes for this role.");
          }
          
          // Continue with question generation using the matched resume
          console.log("Generating questions for:", matchedResume.file_name);
          console.log("Resume text length:", matchedResume.resume_text.length);
          console.log("Requesting questions:", numQuestions);

          const response = await fetch(`${API_CONFIG.RESUME_API_URL}/generate-questions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              job_description: selectedRole,
              resume_text: matchedResume.resume_text,
              candidate_name: matchedResume.file_name, // Use actual file name
              folder_name: selectedRole,
              num_questions: numQuestions,
              existing_count: currentQuestionCount
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: response.statusText }));
            throw new Error(errorData.detail || `Failed to generate questions: ${response.status}`);
          }

          const data = await response.json();
          console.log("Questions generated:", data);
          
          if (questions && questions.questions) {
            const combinedQuestions = {
              ...data.questions,
              questions: [...questions.questions, ...data.questions.questions]
            };
            setQuestions(combinedQuestions);
            setCurrentQuestionCount(combinedQuestions.questions.length);
          } else {
            setQuestions(data.questions);
            setCurrentQuestionCount(data.questions.questions?.length || 0);
          }
          
          setIsExpanded(true);
          setIsLoading(false);
          return; // Exit successfully
        } else if (matches.length > 1) {
          throw new Error(`Multiple resumes match "${searchName}": ${matches.map(m => m.file_name).join(', ')}. Please be more specific.`);
        } else {
          const errorMsg = `Resume "${searchName}" not found in role "${folderName}". 
          
Searched ${resumes.length} resumes with terms: ${searchTerms.join(', ')}.

This usually means:
1. The resume file name doesn't match what the AI returned
2. You're viewing results from a different role
3. The resume was deleted after evaluation

First 10 available resumes in "${folderName}":
${resumes.slice(0, 10).map((r, i) => `${i+1}. ${r.file_name}`).join('\n')}

Please try:
- Selecting the correct role from the dropdown
- Re-running the evaluation for this role
- Checking if the resume exists in S3`;
          
          throw new Error(errorMsg);
        }
      }

      if (!resume.resume_text || resume.resume_text.trim().length === 0) {
        throw new Error("Resume text is empty. Please reload resumes for this role.");
      }

      console.log("Generating questions for:", candidate.name);
      console.log("Resume text length:", resume.resume_text.length);
      console.log("Requesting questions:", numQuestions);

      // Generate questions with the specified count
      const response = await fetch(`${API_CONFIG.RESUME_API_URL}/generate-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_description: selectedRole,
          resume_text: resume.resume_text,
          candidate_name: candidate.name,
          folder_name: selectedRole,
          num_questions: numQuestions,
          existing_count: currentQuestionCount
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `Failed to generate questions: ${response.status}`);
      }

      const data = await response.json();
      console.log("Questions generated:", data);
      
      // If we already have questions, append the new ones
      if (questions && questions.questions) {
        const combinedQuestions = {
          ...data.questions,
          questions: [...questions.questions, ...data.questions.questions]
        };
        setQuestions(combinedQuestions);
        setCurrentQuestionCount(combinedQuestions.questions.length);
      } else {
        setQuestions(data.questions);
        setCurrentQuestionCount(data.questions.questions?.length || 0);
      }
      
      setIsExpanded(true);
    } catch (err) {
      console.error("Error generating questions:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const generateMoreQuestions = () => {
    const remaining = MAX_QUESTIONS - currentQuestionCount;
    const toGenerate = Math.min(INCREMENT_QUESTIONS, remaining);
    generateQuestions(toGenerate);
  };

  const downloadQuestions = async () => {
    if (!questions) return;

    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      
      const candidateName = questions.candidate_name || candidate.name;
      
      // Create new PDF document
      const doc = new jsPDF();
      
      // Set up styling
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Add header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 102, 0);
      doc.text('Interview Questions', margin, yPosition);
      yPosition += 10;
      
      // Add candidate info
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Candidate: ${candidateName}`, margin, yPosition);
      yPosition += 6;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(`Role: ${selectedRole.replace('/', '')}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Total Questions: ${questions.questions.length}`, margin, yPosition);
      yPosition += 10;

      // Add separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Add questions
      questions.questions.forEach((q, idx) => {
        // Check if we need a new page for this question
        checkNewPage(30);
        
        // Question number and text
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 102, 0);
        doc.text(`${q.question_number}.`, margin, yPosition);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const questionLines = doc.splitTextToSize(q.question, maxWidth - 10);
        questionLines.forEach(line => {
          checkNewPage(5);
          doc.text(line, margin + 8, yPosition);
          yPosition += 5;
        });
        
        yPosition += 2;
        
        // Category and focus area
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text(`Category: ${q.category} | Focus: ${q.focus_area}`, margin + 8, yPosition);
        yPosition += 6;
        
        // Suggested answer if present
        if (q.suggested_answer) {
          checkNewPage(15);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 102, 204);
          doc.text('Suggested Answer:', margin + 8, yPosition);
          yPosition += 5;
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(64, 64, 64);
          const answerLines = doc.splitTextToSize(q.suggested_answer, maxWidth - 10);
          answerLines.forEach(line => {
            checkNewPage(4);
            doc.text(line, margin + 8, yPosition);
            yPosition += 4;
          });
          yPosition += 3;
        }
        
        // Add separator between questions
        if (idx < questions.questions.length - 1) {
          checkNewPage(8);
          doc.setDrawColor(230, 230, 230);
          doc.line(margin + 5, yPosition, pageWidth - margin - 5, yPosition);
          yPosition += 8;
        }
      });

      // Add metadata footer on last page
      if (questions.metadata && questions.metadata.execution_time_ms) {
        yPosition += 10;
        checkNewPage(15);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text(`Generated in ${questions.metadata.execution_time_ms}ms`, margin, yPosition);
        yPosition += 4;
        
        // Only show tokens if they exist
        if (questions.metadata.total_tokens !== undefined) {
          const totalTokens = questions.metadata.total_tokens || 0;
          const inputTokens = questions.metadata.input_tokens || 0;
          const outputTokens = questions.metadata.output_tokens || 0;
          doc.text(`Tokens: ${totalTokens.toLocaleString()} (${inputTokens.toLocaleString()} in, ${outputTokens.toLocaleString()} out)`, margin, yPosition);
        }
      }

      // Save the PDF
      const fileName = `${candidateName.replace(/\s+/g, '_')}_interview_questions.pdf`;
      doc.save(fileName);
      
      console.log('✅ Questions exported as PDF successfully');
    } catch (error) {
      console.error('❌ Failed to export questions as PDF:', error);
      alert('Failed to export questions. Please try again.');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileQuestion className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold text-gray-900">Interview Questions</h3>
        </div>
        
        <div className="flex items-center gap-2">
          {questions && (
            <>
              <button
                onClick={downloadQuestions}
                className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                title="Download questions"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {!questions && !isLoading && (
        <button
          onClick={() => generateQuestions(INITIAL_QUESTIONS)}
          disabled={isLoading}
          className="w-full py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Generate 10 Interview Questions
        </button>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-orange-600 animate-spin" />
          <span className="ml-2 text-gray-600">Generating questions...</span>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {questions && isExpanded && (
        <div className="space-y-4 mt-4">
          {/* Display actual candidate name from questions */}
          {questions.candidate_name && questions.candidate_name !== candidate.name && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Candidate Name:</span> {questions.candidate_name}
              </p>
            </div>
          )}
          
          {questions.questions && questions.questions.map((q, idx) => (
            <div key={idx} className="border-l-4 border-orange-500 pl-4 py-3 bg-gray-50 rounded-r-lg">
              <div className="flex items-start gap-2">
                <span className="font-bold text-orange-600 min-w-[24px]">{q.question_number}.</span>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium mb-2">{q.question}</p>
                  <div className="flex gap-3 text-xs text-gray-600 mb-3">
                    <span className="bg-gray-100 px-2 py-1 rounded">{q.category}</span>
                    <span className="italic">{q.focus_area}</span>
                  </div>
                  {q.suggested_answer && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-900 mb-1">Suggested Answer:</p>
                      <p className="text-sm text-blue-800 leading-relaxed">{q.suggested_answer}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {questions.metadata && questions.metadata.execution_time_ms && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p>Generated in {questions.metadata.execution_time_ms}ms</p>
              {questions.metadata.total_tokens !== undefined && questions.metadata.total_tokens !== null && (
                <p>Tokens: {(questions.metadata.total_tokens || 0).toLocaleString()} ({(questions.metadata.input_tokens || 0).toLocaleString()} in, {(questions.metadata.output_tokens || 0).toLocaleString()} out)</p>
              )}
            </div>
          )}

          {/* Generate More Questions Button */}
          {currentQuestionCount < MAX_QUESTIONS && !isLoading && (
            <div className="mt-6 pt-4 border-t-2 border-yellow-300 bg-yellow-50 rounded-lg p-4">
              <button
                onClick={generateMoreQuestions}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span>Generate 5 More Questions</span>
                <span className="text-xs bg-white/20 px-2 py-1 rounded">
                  ({currentQuestionCount}/{MAX_QUESTIONS})
                </span>
              </button>
              <p className="text-xs text-gray-600 text-center mt-2">
                {MAX_QUESTIONS - currentQuestionCount} questions remaining
              </p>
            </div>
          )}

          {currentQuestionCount >= MAX_QUESTIONS && (
            <div className="mt-6 pt-4 border-t-2 border-green-300 bg-green-50 rounded-lg p-3">
              <p className="text-sm text-green-800 text-center font-semibold">
                ✓ Maximum questions reached ({MAX_QUESTIONS}/{MAX_QUESTIONS})
              </p>
            </div>
          )}
        </div>
      )}

      {questions && !isExpanded && (
        <div className="text-sm text-gray-600 text-center py-2">
          {questions.candidate_name && (
            <p className="font-semibold text-gray-800 mb-1">{questions.candidate_name}</p>
          )}
          {questions.questions?.length || 0} questions generated - Click to expand
        </div>
      )}
    </div>
  );
};

export default CandidateQuestions;
